import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@munifocus/ui';
import {
  COUNCIL_AUTHORIZATION_KINDS,
  councilDecisionProblem,
  type CouncilAuthorizationKind,
  type CouncilAuthorizationView,
  type CouncilDecisionInput,
  type Form4NameApprovalView,
  type Form4NameDecision,
} from '@munifocus/contracts-gov';
import {
  useDecideCouncilAuthorization,
  useDecideForm4NameApproval,
  useRequestCouncilAuthorization,
  useRequestForm4NameApproval,
} from '../../lib/clerk-api';

/**
 * The Clerk answers a Form 4 name request: their name may go on the notice,
 * or not — and if not, why, because that is what the officer acts on.
 */
export function Form4DecisionDialog({
  approval,
  onClose,
}: {
  approval: Form4NameApprovalView;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const decide = useDecideForm4NameApproval();
  const [decision, setDecision] = useState<Form4NameDecision>('approve');
  const [comment, setComment] = useState('');
  const missingReason = decision === 'decline' && !comment.trim();

  const submit = () =>
    decide.mutate(
      {
        fileId: approval.fileId,
        id: approval.id,
        dto: { decision, ...(comment.trim() ? { comment: comment.trim() } : {}) },
      },
      { onSuccess: onClose },
    );

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.clerk.form4.decideTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">
            {t('gov.clerk.form4.decideLead', {
              file: approval.fileReference,
              name: approval.requestedByName,
            })}
          </Typography>
          {approval.requestNote && (
            <Typography variant="body2" color="text.secondary">
              {approval.requestNote}
            </Typography>
          )}
          <ToggleButtonGroup
            exclusive
            size="small"
            value={decision}
            onChange={(_e, value: Form4NameDecision | null) => value && setDecision(value)}
            aria-label={t('gov.clerk.decision')}
          >
            <ToggleButton value="approve">{t('gov.clerk.form4.optionApprove')}</ToggleButton>
            <ToggleButton value="decline">{t('gov.clerk.form4.optionDecline')}</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={
              decision === 'decline'
                ? t('gov.clerk.reasonRequired')
                : t('gov.clerk.commentOptional')
            }
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={decide.isPending || missingReason}
          color={decision === 'decline' ? 'error' : 'primary'}
        >
          {decision === 'approve' ? t('gov.clerk.form4.approve') : t('gov.clerk.form4.decline')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * The Clerk records what council decided on remedial work. Authorizing needs
 * the meeting date and the resolution reference: council binds the
 * municipality only by resolution, and the record has to say which.
 */
export function CouncilDecisionDialog({
  authorization,
  onClose,
}: {
  authorization: CouncilAuthorizationView;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const decide = useDecideCouncilAuthorization();
  const [decision, setDecision] = useState<CouncilDecisionInput>('authorize');
  const [meetingDate, setMeetingDate] = useState('');
  const [resolutionRef, setResolutionRef] = useState('');
  const [notes, setNotes] = useState('');
  const problem = councilDecisionProblem({
    decision,
    councilMeetingDate: meetingDate,
    resolutionRef,
    notes,
  });

  const submit = () =>
    decide.mutate(
      {
        fileId: authorization.fileId,
        id: authorization.id,
        dto: {
          decision,
          ...(meetingDate ? { councilMeetingDate: meetingDate } : {}),
          ...(resolutionRef.trim() ? { resolutionRef: resolutionRef.trim() } : {}),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
      },
      { onSuccess: onClose },
    );

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.clerk.council.decideTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">
            {t('gov.clerk.council.decideLead', {
              file: authorization.fileReference,
              work: t(`gov.clerk.council.kinds.${authorization.kind}`),
              name: authorization.requestedByName,
            })}
          </Typography>
          {authorization.requestNote && (
            <Typography variant="body2" color="text.secondary">
              {authorization.requestNote}
            </Typography>
          )}
          <ToggleButtonGroup
            exclusive
            size="small"
            value={decision}
            onChange={(_e, value: CouncilDecisionInput | null) => value && setDecision(value)}
            aria-label={t('gov.clerk.decision')}
          >
            <ToggleButton value="authorize">{t('gov.clerk.council.authorize')}</ToggleButton>
            <ToggleButton value="decline">{t('gov.clerk.council.decline')}</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            fullWidth
            size="small"
            type="date"
            label={t('gov.clerk.council.meetingDate')}
            slotProps={{ inputLabel: { shrink: true } }}
            value={meetingDate}
            onChange={(e) => setMeetingDate(e.target.value)}
            required={decision === 'authorize'}
          />
          <TextField
            fullWidth
            size="small"
            label={t('gov.clerk.council.resolutionRef')}
            value={resolutionRef}
            onChange={(e) => setResolutionRef(e.target.value)}
            required={decision === 'authorize'}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={t('gov.clerk.council.notes')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          {problem && <Alert severity="info">{t(`gov.clerk.council.problems.${problem}`)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={decide.isPending || Boolean(problem)}
          color={decision === 'decline' ? 'error' : 'primary'}
        >
          {decision === 'authorize'
            ? t('gov.clerk.council.recordAuthorization')
            : t('gov.clerk.council.recordRefusal')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** An officer asks the Clerk about their name on this file's Form 4. */
export function RequestForm4Dialog({
  fileId,
  formId,
  onClose,
}: {
  fileId: string;
  formId?: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const request = useRequestForm4NameApproval(fileId);
  const [note, setNote] = useState('');
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.clerk.form4.requestTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">{t('gov.clerk.form4.requestLead')}</Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={t('gov.clerk.noteOptional')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button
          variant="contained"
          disabled={request.isPending}
          onClick={() =>
            request.mutate(
              { ...(note.trim() ? { note: note.trim() } : {}), ...(formId ? { formId } : {}) },
              { onSuccess: onClose },
            )
          }
        >
          {t('gov.clerk.form4.request')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** An officer asks the Clerk to record council's decision on remedial work. */
export function RequestCouncilDialog({ fileId, onClose }: { fileId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const request = useRequestCouncilAuthorization(fileId);
  const [kind, setKind] = useState<CouncilAuthorizationKind>('clean_up');
  const [note, setNote] = useState('');
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.clerk.council.requestTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">{t('gov.clerk.council.requestLead')}</Typography>
          <TextField
            select
            fullWidth
            size="small"
            label={t('gov.clerk.council.kind')}
            value={kind}
            onChange={(e) => setKind(e.target.value as CouncilAuthorizationKind)}
          >
            {COUNCIL_AUTHORIZATION_KINDS.map((k) => (
              <MenuItem key={k} value={k}>
                {t(`gov.clerk.council.kinds.${k}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={t('gov.clerk.noteOptional')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button
          variant="contained"
          disabled={request.isPending}
          onClick={() =>
            request.mutate(
              { kind, ...(note.trim() ? { note: note.trim() } : {}) },
              { onSuccess: onClose },
            )
          }
        >
          {t('gov.clerk.council.request')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
